import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Layers3, X } from 'lucide-react';
import { useDesktopMatterTabs } from './DesktopMatterTabsContext';
import type { MatterTab } from './tabState';
import './desktopMatterTabs.css';

export default function DesktopMatterTabStrip() {
  const tabs = useDesktopMatterTabs();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (!overflowRef.current?.contains(event.target as Node)) setOverflowOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [overflowOpen]);

  if (!tabs.enabled || !tabs.state.home || tabs.state.matters.length === 0) return null;

  const activateMatter = (tab: MatterTab) => {
    tabs.activate(tab.key);
    setOverflowOpen(false);
  };

  return (
    <div className="desktop-matter-tabs" role="tablist" aria-label="Open Valentine matters">
      <button
        type="button"
        role="tab"
        aria-selected={tabs.state.activeKey === 'home'}
        className="desktop-matter-tabs__home"
        onClick={() => tabs.activate('home')}
      >
        <Layers3 size={14} aria-hidden="true" />
        {tabs.state.home.label}
      </button>

      <div className="desktop-matter-tabs__scroll">
        {tabs.state.matters.map((tab) => (
          <div className="desktop-matter-tabs__matter" data-active={tabs.state.activeKey === tab.key} key={tab.key}>
            <button
              type="button"
              role="tab"
              aria-selected={tabs.state.activeKey === tab.key}
              title={tab.title}
              onClick={() => activateMatter(tab)}
            >
              {tab.title}
            </button>
            <button
              type="button"
              aria-label={`Close ${tab.title}`}
              className="desktop-matter-tabs__close"
              onClick={() => tabs.closeMatter(tab.matterId)}
            >
              <X size={13} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>

      <div className="desktop-matter-tabs__overflow" ref={overflowRef}>
        <button
          type="button"
          aria-label={`Show all ${tabs.state.matters.length} matter tabs`}
          aria-expanded={overflowOpen}
          onClick={() => setOverflowOpen((open) => !open)}
        >
          {tabs.state.matters.length}
          <ChevronDown size={14} aria-hidden="true" />
        </button>
        {overflowOpen && (
          <div role="menu" aria-label="All open matter tabs" className="desktop-matter-tabs__overflow-menu">
            {tabs.state.matters.map((tab) => (
              <button
                type="button"
                role="menuitem"
                aria-current={tabs.state.activeKey === tab.key ? 'page' : undefined}
                key={tab.key}
                onClick={() => activateMatter(tab)}
              >
                {tab.title}
              </button>
            ))}
          </div>
        )}
      </div>

      <button type="button" className="desktop-matter-tabs__clear" onClick={() => tabs.closeAll()}>
        Close all matter tabs
      </button>
    </div>
  );
}
